// Layer 4 (UI) — Production planning (locked comp
// `.impeccable/mocks/decision/planning.html`, variant A · Year 1 against
// budget, picked 25 Sep with C's budget row): one status band, then Year 1
// feed against budget beside Month 4 to close, then Paul's three tabs as the
// drill-downs. Every figure reads Scene.transform.ts through the sibling
// transforms; this file composes and routes.

import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  HelpAnchor,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  SurfacePageTabs,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { projectTileBySlug } from '@/versions/v1/shared/project-seed';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import { DowntimeCard } from '../downtime/DowntimeCard';
import { CascadeTable } from './CascadeTable';
import { MonthClose } from './MonthClose';
import { monthCloseRead } from './MonthClose.transform';
import { PlanningBand, PlanningPeriodChip, SYN_CHIP } from './PlanningBand';
import { planningBandCells, planningPeriod, planningTabCounts } from './PlanningBand.transform';
import { AnnualPlanTable, MonthlyPlanTable, ScenarioTable } from './PlanningTables';
import { annualTableRead, hierarchyRead, monthlyTableRead, scenarioTableRead } from './PlanningTables.transform';
import {
  PLANNING_TABS,
  annualPlanView,
  monthlyPlanView,
  planningCopy,
  planningTabFromSearch,
  rollingForecastView,
  type PlanningScenarioName,
  type PlanningTab,
} from './Scene.transform';
import { YearAgainstBudgetChart } from './YearAgainstBudget';
import { yearAgainstBudgetView } from './YearAgainstBudget.transform';

export function PlanningScene() {
  const { projectId } = useParams<{ projectId: string }>();
  const project = projectTileBySlug(projectId);
  const copy = planningCopy();
  // #736: Planning reads the session cursor, the same one `/capacity` projects
  // through (`useScenario()` state → `capacityPlantState`), so moving the replay
  // cursor moves both routes' figures together.
  const { state: sessionState } = useScenario();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<PlanningTab>('monthly');
  const [scenario, setScenario] = useState<PlanningScenarioName>('Central');

  useEffect(() => {
    setTab(planningTabFromSearch(`?${searchParams.toString()}`));
  }, [searchParams]);

  useEffect(() => {
    document.title = project ? `${copy.title} · ${project.name}` : `${copy.title} · OptiFlow`;
  }, [copy.title, project]);

  const selectTab = (next: PlanningTab) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  if (!project) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody>
          <SurfacePageHeader title="Plant not found" />
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  const monthly = monthlyPlanView(sessionState);
  const annual = annualPlanView(sessionState);
  const rolling = rollingForecastView(sessionState);
  const counts = planningTabCounts(monthly, annual, rolling);
  const tabItems = PLANNING_TABS.map((item) => ({ ...item, count: counts[item.id] }));
  const period = planningPeriod();
  const hierarchy = hierarchyRead(annual);

  return (
    <SurfacePageLayout width="full">
      <Commentable name="PlanningScene">
        <SurfacePageBody className="min-h-[calc(100vh-3.2rem)] space-y-3" width="full">
          <SurfacePageHeader
            title={copy.title}
            titleSize="large"
            actions={
              <>
                <PlanningPeriodChip lead={period.lead} range={period.range} />
                {SYN_CHIP}
                <HelpAnchor doc="planning/overview" label="Planning" />
              </>
            }
          />
          <PlanningBand cells={planningBandCells(monthly, annual, rolling, sessionState)} />
          <div className="grid min-w-0 gap-3 xl:grid-cols-12 xl:items-stretch">
            <DowntimeCard
              title="Year 1 feed against budget"
              meta={`cumulative dst · ${period.range}`}
              actions={SYN_CHIP}
              className="xl:col-span-8"
              aria-label="Year 1 feed against budget"
            >
              <YearAgainstBudgetChart view={yearAgainstBudgetView(monthly, rolling)} selected={scenario} />
            </DowntimeCard>
            <MonthClose
              read={monthCloseRead(monthly, annual, rolling)}
              selected={scenario}
              onSelect={setScenario}
              className="xl:col-span-4"
            />
          </div>
          <SurfacePageTabs aria-label="Planning" items={tabItems} value={tab} onChange={selectTab} variant="ink" className="pt-3" />
          {tab === 'monthly' ? <MonthlyPlanTable read={monthlyTableRead(monthly)} /> : null}
          {tab === 'annual' ? (
            // The 8/4 split needs the annual table's bullet column to fit;
            // below 1400 px the hierarchy stacks under the full-width table.
            <div className="grid min-w-0 gap-3 min-[1400px]:grid-cols-12 min-[1400px]:items-start">
              <AnnualPlanTable rows={annualTableRead(annual)} className="min-[1400px]:col-span-8" />
              <CascadeTable rows={hierarchy.rows} planEqualsBudget={hierarchy.planEqualsBudget} capDecode={hierarchy.capDecode} className="min-[1400px]:col-span-4" />
            </div>
          ) : null}
          {tab === 'forecast' ? <ScenarioTable read={scenarioTableRead(monthly, annual, rolling, sessionState)} /> : null}
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
